import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, School, Award, BookOpen, Clock, Settings, Edit, Save, X, Crown, Calendar, Lock, Unlock, Trophy, Star, CheckCircle, Target, TrendingUp, Zap, Bookmark, Heart, Key, CreditCard, HelpCircle, Bell, LogOut, ChevronRight, AlertTriangle } from 'lucide-react';
import { PasswordChangeModal } from '../components/modals/PasswordChangeModal';
import { EmailUpdateModal } from '../components/modals/EmailUpdateModal';
import { LogoutConfirmModal } from '../components/modals/LogoutConfirmModal';
import { useToast } from '../components/Toast';
export function Profile() {
  const navigate = useNavigate();
  const {
    showToast,
    ToastContainer
  } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [showBadgeDetails, setShowBadgeDetails] = useState<number | null>(null);
  const [showUnlockMoment, setShowUnlockMoment] = useState(false);
  const [unlockedBadge, setUnlockedBadge] = useState<Achievement | null>(null);
  // Modal states
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  // Email verification state
  const [isEmailPending, setIsEmailPending] = useState(false);
  const [profileData, setProfileData] = useState({
    name: 'Ahmet Yılmaz',
    email: 'ahmet.yilmaz@example.com',
    school: 'Atatürk Anadolu Lisesi',
    grade: '8. Sınıf',
    targetExam: 'LGS 2024',
    membershipType: 'Premium',
    membershipStart: '2023-09-01',
    membershipEnd: '2024-09-01'
  });
  // Performance stats
  const stats = [{
    title: 'Çözülen Soru',
    value: '1,458',
    icon: BookOpen,
    description: 'Toplam çözülen soru sayısı',
    gradientFrom: 'from-blue-500',
    gradientTo: 'to-cyan-500'
  }, {
    title: 'En Yüksek Net',
    value: '82.5',
    icon: TrendingUp,
    description: 'Tüm denemelerdeki en yüksek net',
    gradientFrom: 'from-green-500',
    gradientTo: 'to-emerald-500'
  }, {
    title: 'En Başarılı Ders',
    value: 'Matematik',
    icon: Star,
    description: 'En yüksek başarı oranına sahip ders',
    gradientFrom: 'from-yellow-500',
    gradientTo: 'to-amber-500'
  }, {
    title: 'Toplam Çalışma',
    value: '256 saat',
    icon: Clock,
    description: 'Platformda geçirilen toplam süre',
    gradientFrom: 'from-purple-500',
    gradientTo: 'to-violet-500'
  }];
  // Badge/Achievement types and data
  interface Achievement {
    id: number;
    name: string;
    description: string;
    tier: 'bronze' | 'silver' | 'gold';
    category: 'performance' | 'consistency' | 'mastery' | 'special';
    icon: React.ElementType;
    unlocked: boolean;
    progress: number;
    target: number;
    unlockedDate?: string;
    nextTier?: {
      name: string;
      target: number;
    };
  }
  const achievements: Achievement[] = [{
    id: 1,
    name: 'Soru Çözücü',
    description: 'Platformda 100 soru çöz',
    tier: 'bronze',
    category: 'performance',
    icon: BookOpen,
    unlocked: true,
    progress: 1458,
    target: 100,
    unlockedDate: '2023-10-15',
    nextTier: {
      name: 'Soru Ustası',
      target: 1000
    }
  }, {
    id: 2,
    name: 'Soru Ustası',
    description: 'Platformda 1000 soru çöz',
    tier: 'silver',
    category: 'performance',
    icon: BookOpen,
    unlocked: true,
    progress: 1458,
    target: 1000,
    unlockedDate: '2023-12-20',
    nextTier: {
      name: 'Soru Efsanesi',
      target: 5000
    }
  }, {
    id: 3,
    name: 'Soru Efsanesi',
    description: 'Platformda 5000 soru çöz',
    tier: 'gold',
    category: 'performance',
    icon: BookOpen,
    unlocked: false,
    progress: 1458,
    target: 5000
  }, {
    id: 4,
    name: 'Çalışkan Öğrenci',
    description: '10 saat çalış',
    tier: 'bronze',
    category: 'consistency',
    icon: Clock,
    unlocked: true,
    progress: 256,
    target: 10,
    unlockedDate: '2023-09-10',
    nextTier: {
      name: 'Disiplinli Öğrenci',
      target: 50
    }
  }, {
    id: 5,
    name: 'Disiplinli Öğrenci',
    description: '50 saat çalış',
    tier: 'silver',
    category: 'consistency',
    icon: Clock,
    unlocked: true,
    progress: 256,
    target: 50,
    unlockedDate: '2023-11-05',
    nextTier: {
      name: 'Azimli Öğrenci',
      target: 200
    }
  }, {
    id: 6,
    name: 'Azimli Öğrenci',
    description: '200 saat çalış',
    tier: 'gold',
    category: 'consistency',
    icon: Clock,
    unlocked: true,
    progress: 256,
    target: 200
  }, {
    id: 7,
    name: 'Matematik Öğrencisi',
    description: 'Matematikte 50 soru çöz',
    tier: 'bronze',
    category: 'mastery',
    icon: Target,
    unlocked: true,
    progress: 350,
    target: 50,
    unlockedDate: '2023-09-25',
    nextTier: {
      name: 'Matematik Uzmanı',
      target: 250
    }
  }, {
    id: 8,
    name: 'Matematik Uzmanı',
    description: 'Matematikte 250 soru çöz',
    tier: 'silver',
    category: 'mastery',
    icon: Target,
    unlocked: true,
    progress: 350,
    target: 250,
    unlockedDate: '2024-01-10',
    nextTier: {
      name: 'Matematik Dehası',
      target: 1000
    }
  }, {
    id: 9,
    name: 'Matematik Dehası',
    description: 'Matematikte 1000 soru çöz',
    tier: 'gold',
    category: 'mastery',
    icon: Target,
    unlocked: false,
    progress: 350,
    target: 1000
  }, {
    id: 10,
    name: 'İlk Deneme',
    description: 'İlk deneme sınavını tamamla',
    tier: 'bronze',
    category: 'special',
    icon: CheckCircle,
    unlocked: true,
    progress: 1,
    target: 1,
    unlockedDate: '2023-10-05',
    nextTier: {
      name: '5 Deneme',
      target: 5
    }
  }, {
    id: 11,
    name: '5 Deneme',
    description: '5 deneme sınavını tamamla',
    tier: 'silver',
    category: 'special',
    icon: CheckCircle,
    unlocked: true,
    progress: 8,
    target: 5,
    unlockedDate: '2023-12-10',
    nextTier: {
      name: '20 Deneme',
      target: 20
    }
  }, {
    id: 12,
    name: '20 Deneme',
    description: '20 deneme sınavını tamamla',
    tier: 'gold',
    category: 'special',
    icon: CheckCircle,
    unlocked: false,
    progress: 8,
    target: 20
  }];
  // Handle successful password change
  const handlePasswordChangeSuccess = () => {
    showToast('Şifreniz başarıyla güncellendi', 'success');
  };
  // Handle successful email update
  const handleEmailUpdateSuccess = (newEmail: string) => {
    setIsEmailPending(true);
    showToast('E-posta güncelleme talebiniz alındı', 'success');
    // In a real app, this would be handled by checking if the email is verified
    // For now, we'll simulate verification after 5 seconds
    setTimeout(() => {
      setProfileData({
        ...profileData,
        email: newEmail
      });
      setIsEmailPending(false);
      showToast('E-posta adresiniz başarıyla güncellendi', 'success');
    }, 5000);
  };
  // Handle logout
  const handleLogout = () => {
    // In a real app, this would call an API to log out
    // For now, just navigate to the login page (which is home for our demo)
    navigate('/');
  };
  // Handle badge click to show details
  const handleBadgeClick = (id: number) => {
    setShowBadgeDetails(id);
  };
  // Handle ESC key press to close modals
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (showBadgeDetails !== null) {
          setShowBadgeDetails(null);
        }
        if (showUnlockMoment) {
          setShowUnlockMoment(false);
        }
      }
    };
    // Add event listener for ESC key
    document.addEventListener('keydown', handleEscKey);
    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showBadgeDetails, showUnlockMoment]);
  // Handle outside click for badge details modal
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // If clicking outside the modal content, close it
      if (showBadgeDetails !== null && !target.closest('.badge-details-content')) {
        setShowBadgeDetails(null);
      }
      // If clicking outside the unlock moment modal content, close it
      if (showUnlockMoment && !target.closest('.unlock-moment-content')) {
        setShowUnlockMoment(false);
      }
    };
    // Add event listener for clicks
    document.addEventListener('mousedown', handleOutsideClick);
    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [showBadgeDetails, showUnlockMoment]);
  // Get tier-specific styling
  const getTierStyle = (tier: 'bronze' | 'silver' | 'gold', unlocked: boolean) => {
    if (!unlocked) {
      return {
        bg: 'bg-gray-800',
        ring: 'ring-gray-700',
        text: 'text-gray-500',
        gradient: 'from-gray-700 to-gray-800'
      };
    }
    switch (tier) {
      case 'bronze':
        return {
          bg: 'bg-amber-900',
          ring: 'ring-amber-700',
          text: 'text-amber-500',
          gradient: 'from-amber-700 to-amber-900'
        };
      case 'silver':
        return {
          bg: 'bg-slate-300',
          ring: 'ring-slate-400',
          text: 'text-slate-300',
          gradient: 'from-slate-400 to-slate-600'
        };
      case 'gold':
        return {
          bg: 'bg-yellow-600',
          ring: 'ring-yellow-500',
          text: 'text-yellow-500',
          gradient: 'from-yellow-500 to-amber-600'
        };
      default:
        return {
          bg: 'bg-purple-900',
          ring: 'ring-purple-700',
          text: 'text-purple-500',
          gradient: 'from-purple-700 to-purple-900'
        };
    }
  };
  return <div className="space-y-8" data-id="element-2660">
      <h1 className="text-2xl font-bold" data-id="element-2661">Profil</h1>
      {/* Profile Header */}
      <div className="relative p-[1px] rounded-lg overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600" data-id="element-2662">
        <div className="relative p-6 bg-black/90 rounded-lg" data-id="element-2663">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6" data-id="element-2664">
            <div className="relative" data-id="element-2665">
              <img src="https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=160&h=160&fit=crop&crop=faces" alt="Profile" className="w-24 h-24 rounded-full" data-id="element-2666" />
              <button className="absolute -bottom-1 -right-1 p-1.5 rounded-full bg-purple-600 text-white hover:bg-purple-700 transition-colors" data-id="element-2667">
                <Edit className="w-4 h-4" data-id="element-2668" />
              </button>
            </div>
            <div className="flex-1 min-w-0" data-id="element-2669">
              {isEditing ? <div className="space-y-4" data-id="element-2670">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2671">
                    <div className="space-y-2" data-id="element-2672">
                      <label className="text-sm text-gray-400" data-id="element-2673">İsim</label>
                      <input type="text" value={profileData.name} onChange={e => setProfileData({
                    ...profileData,
                    name: e.target.value
                  })} className="w-full bg-white/5 text-white rounded-lg px-4 py-2" data-id="element-2674" />
                    </div>
                    <div className="space-y-2" data-id="element-2675">
                      <label className="text-sm text-gray-400" data-id="element-2676">Email</label>
                      <input type="email" value={profileData.email} onChange={e => setProfileData({
                    ...profileData,
                    email: e.target.value
                  })} className="w-full bg-white/5 text-white rounded-lg px-4 py-2" data-id="element-2677" />
                    </div>
                    <div className="space-y-2" data-id="element-2678">
                      <label className="text-sm text-gray-400" data-id="element-2679">Okul</label>
                      <input type="text" value={profileData.school} onChange={e => setProfileData({
                    ...profileData,
                    school: e.target.value
                  })} className="w-full bg-white/5 text-white rounded-lg px-4 py-2" data-id="element-2680" />
                    </div>
                    <div className="space-y-2" data-id="element-2681">
                      <label className="text-sm text-gray-400" data-id="element-2682">Sınıf</label>
                      <input type="text" value={profileData.grade} onChange={e => setProfileData({
                    ...profileData,
                    grade: e.target.value
                  })} className="w-full bg-white/5 text-white rounded-lg px-4 py-2" data-id="element-2683" />
                    </div>
                  </div>
                  <div className="flex justify-end gap-3" data-id="element-2684">
                    <button onClick={() => setIsEditing(false)} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors" data-id="element-2685">
                      <X className="w-4 h-4" data-id="element-2686" />
                      İptal
                    </button>
                    <button onClick={() => {
                  setIsEditing(false);
                  showToast('Profil bilgileriniz güncellendi', 'success');
                }} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 transition-colors" data-id="element-2687">
                      <Save className="w-4 h-4" data-id="element-2688" />
                      Kaydet
                    </button>
                  </div>
                </div> : <div className="space-y-4" data-id="element-2689">
                  <div className="flex items-center justify-between" data-id="element-2690">
                    <h2 className="text-2xl font-bold" data-id="element-2691">{profileData.name}</h2>
                    <button onClick={() => setIsEditing(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors" data-id="element-2692">
                      <Edit className="w-4 h-4" data-id="element-2693" />
                      Düzenle
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2" data-id="element-2694">
                    <div className="flex items-center gap-2 text-gray-400" data-id="element-2695">
                      <Mail className="w-4 h-4" data-id="element-2696" />
                      <span data-id="element-2697">
                        {profileData.email}
                        {isEmailPending && <span className="ml-2 text-xs text-amber-400" data-id="element-2698">
                            (Doğrulama Bekleniyor)
                          </span>}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-400" data-id="element-2699">
                      <School className="w-4 h-4" data-id="element-2700" />
                      <span data-id="element-2701">{profileData.school}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-400" data-id="element-2702">
                      <User className="w-4 h-4" data-id="element-2703" />
                      <span data-id="element-2704">{profileData.grade}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-400" data-id="element-2705">
                      <Award className="w-4 h-4" data-id="element-2706" />
                      <span data-id="element-2707">{profileData.targetExam}</span>
                    </div>
                  </div>
                </div>}
            </div>
          </div>
        </div>
      </div>
      {/* Performance Stats */}
      <div data-id="element-2708">
        <h2 className="text-xl font-bold mb-4" data-id="element-2709">Performans Özeti</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2710">
          {stats.map(stat => <div key={stat.title} className={`relative p-[1px] rounded-lg overflow-hidden bg-gradient-to-r ${stat.gradientFrom} ${stat.gradientTo} group`} data-id="element-2711">
              <div className="relative p-5 bg-black/90 rounded-lg h-full" data-id="element-2712">
                <div className="flex flex-col h-full" data-id="element-2713">
                  <div className="flex items-center justify-between mb-2" data-id="element-2714">
                    <h3 className="text-sm font-medium text-gray-400" data-id="element-2715">
                      {stat.title}
                    </h3>
                    <stat.icon className="w-5 h-5 text-gray-400" data-id="element-2716" />
                  </div>
                  <p className="text-2xl font-bold mb-1" data-id="element-2717">{stat.value}</p>
                  <p className="text-xs text-gray-500 mt-auto" data-id="element-2718">
                    {stat.description}
                  </p>
                </div>
              </div>
            </div>)}
        </div>
      </div>
      {/* Achievements and Badges */}
      <div data-id="element-2719">
        <h2 className="text-xl font-bold mb-4" data-id="element-2720">Başarımlar ve Rozetler</h2>
        {/* Badges by category */}
        <div className="space-y-6" data-id="element-2721">
          {/* Performance Badges */}
          <div className="space-y-3" data-id="element-2722">
            <h3 className="text-lg font-medium text-gray-300" data-id="element-2723">
              Performans Rozetleri
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-id="element-2724">
              {achievements.filter(badge => badge.category === 'performance').map(badge => {
              const tierStyle = getTierStyle(badge.tier, badge.unlocked);
              return <div key={badge.id} onClick={() => handleBadgeClick(badge.id)} className={`relative cursor-pointer group p-[1px] rounded-lg overflow-hidden bg-gradient-to-r ${tierStyle.gradient} hover:shadow-lg transition-all duration-300`} data-id="element-2725">
                      <div className="relative p-4 bg-black/90 rounded-lg flex flex-col items-center justify-center h-full" data-id="element-2726">
                        <div className={`relative w-12 h-12 ${tierStyle.bg} rounded-full flex items-center justify-center mb-2 ring-2 ${tierStyle.ring}`} data-id="element-2727">
                          <badge.icon className={`w-6 h-6 ${tierStyle.text}`} data-id="element-2728" />
                          {!badge.unlocked && <div className="absolute -bottom-1 -right-1 bg-gray-800 rounded-full p-0.5" data-id="element-2729">
                              <Lock className="w-3 h-3 text-gray-500" data-id="element-2730" />
                            </div>}
                        </div>
                        <h4 className={`text-xs font-medium text-center ${badge.unlocked ? 'text-white' : 'text-gray-500'}`} data-id="element-2731">
                          {badge.name}
                        </h4>
                        <p className={`text-[10px] mt-1 text-center ${badge.unlocked ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-2732">
                          {badge.tier === 'bronze' ? 'Bronz' : badge.tier === 'silver' ? 'Gümüş' : 'Altın'}
                        </p>
                      </div>
                    </div>;
            })}
            </div>
          </div>
          {/* Consistency Badges */}
          <div className="space-y-3" data-id="element-2733">
            <h3 className="text-lg font-medium text-gray-300" data-id="element-2734">
              İstikrar Rozetleri
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-id="element-2735">
              {achievements.filter(badge => badge.category === 'consistency').map(badge => {
              const tierStyle = getTierStyle(badge.tier, badge.unlocked);
              return <div key={badge.id} onClick={() => handleBadgeClick(badge.id)} className={`relative cursor-pointer group p-[1px] rounded-lg overflow-hidden bg-gradient-to-r ${tierStyle.gradient} hover:shadow-lg transition-all duration-300`} data-id="element-2736">
                      <div className="relative p-4 bg-black/90 rounded-lg flex flex-col items-center justify-center h-full" data-id="element-2737">
                        <div className={`relative w-12 h-12 ${tierStyle.bg} rounded-full flex items-center justify-center mb-2 ring-2 ${tierStyle.ring}`} data-id="element-2738">
                          <badge.icon className={`w-6 h-6 ${tierStyle.text}`} data-id="element-2739" />
                          {!badge.unlocked && <div className="absolute -bottom-1 -right-1 bg-gray-800 rounded-full p-0.5" data-id="element-2740">
                              <Lock className="w-3 h-3 text-gray-500" data-id="element-2741" />
                            </div>}
                        </div>
                        <h4 className={`text-xs font-medium text-center ${badge.unlocked ? 'text-white' : 'text-gray-500'}`} data-id="element-2742">
                          {badge.name}
                        </h4>
                        <p className={`text-[10px] mt-1 text-center ${badge.unlocked ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-2743">
                          {badge.tier === 'bronze' ? 'Bronz' : badge.tier === 'silver' ? 'Gümüş' : 'Altın'}
                        </p>
                      </div>
                    </div>;
            })}
            </div>
          </div>
          {/* Mastery Badges */}
          <div className="space-y-3" data-id="element-2744">
            <h3 className="text-lg font-medium text-gray-300" data-id="element-2745">
              Uzmanlık Rozetleri
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-id="element-2746">
              {achievements.filter(badge => badge.category === 'mastery').map(badge => {
              const tierStyle = getTierStyle(badge.tier, badge.unlocked);
              return <div key={badge.id} onClick={() => handleBadgeClick(badge.id)} className={`relative cursor-pointer group p-[1px] rounded-lg overflow-hidden bg-gradient-to-r ${tierStyle.gradient} hover:shadow-lg transition-all duration-300`} data-id="element-2747">
                      <div className="relative p-4 bg-black/90 rounded-lg flex flex-col items-center justify-center h-full" data-id="element-2748">
                        <div className={`relative w-12 h-12 ${tierStyle.bg} rounded-full flex items-center justify-center mb-2 ring-2 ${tierStyle.ring}`} data-id="element-2749">
                          <badge.icon className={`w-6 h-6 ${tierStyle.text}`} data-id="element-2750" />
                          {!badge.unlocked && <div className="absolute -bottom-1 -right-1 bg-gray-800 rounded-full p-0.5" data-id="element-2751">
                              <Lock className="w-3 h-3 text-gray-500" data-id="element-2752" />
                            </div>}
                        </div>
                        <h4 className={`text-xs font-medium text-center ${badge.unlocked ? 'text-white' : 'text-gray-500'}`} data-id="element-2753">
                          {badge.name}
                        </h4>
                        <p className={`text-[10px] mt-1 text-center ${badge.unlocked ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-2754">
                          {badge.tier === 'bronze' ? 'Bronz' : badge.tier === 'silver' ? 'Gümüş' : 'Altın'}
                        </p>
                      </div>
                    </div>;
            })}
            </div>
          </div>
          {/* Special Badges */}
          <div className="space-y-3" data-id="element-2755">
            <h3 className="text-lg font-medium text-gray-300" data-id="element-2756">Özel Rozetler</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-id="element-2757">
              {achievements.filter(badge => badge.category === 'special').map(badge => {
              const tierStyle = getTierStyle(badge.tier, badge.unlocked);
              return <div key={badge.id} onClick={() => handleBadgeClick(badge.id)} className={`relative cursor-pointer group p-[1px] rounded-lg overflow-hidden bg-gradient-to-r ${tierStyle.gradient} hover:shadow-lg transition-all duration-300`} data-id="element-2758">
                      <div className="relative p-4 bg-black/90 rounded-lg flex flex-col items-center justify-center h-full" data-id="element-2759">
                        <div className={`relative w-12 h-12 ${tierStyle.bg} rounded-full flex items-center justify-center mb-2 ring-2 ${tierStyle.ring}`} data-id="element-2760">
                          <badge.icon className={`w-6 h-6 ${tierStyle.text}`} data-id="element-2761" />
                          {!badge.unlocked && <div className="absolute -bottom-1 -right-1 bg-gray-800 rounded-full p-0.5" data-id="element-2762">
                              <Lock className="w-3 h-3 text-gray-500" data-id="element-2763" />
                            </div>}
                        </div>
                        <h4 className={`text-xs font-medium text-center ${badge.unlocked ? 'text-white' : 'text-gray-500'}`} data-id="element-2764">
                          {badge.name}
                        </h4>
                        <p className={`text-[10px] mt-1 text-center ${badge.unlocked ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-2765">
                          {badge.tier === 'bronze' ? 'Bronz' : badge.tier === 'silver' ? 'Gümüş' : 'Altın'}
                        </p>
                      </div>
                    </div>;
            })}
            </div>
          </div>
        </div>
      </div>
      {/* Account Settings */}
      <div data-id="element-2766">
        <h2 className="text-xl font-bold mb-4" data-id="element-2767">Hesap Ayarları</h2>
        <div className="relative p-[1px] rounded-lg overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600" data-id="element-2768">
          <div className="relative p-6 bg-black/90 rounded-lg" data-id="element-2769">
            <div className="space-y-4" data-id="element-2770">
              <h3 className="text-lg font-medium" data-id="element-2771">Güvenlik</h3>
              <button onClick={() => setShowPasswordModal(true)} className="w-full flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors" data-id="element-2772">
                <div className="flex items-center gap-3" data-id="element-2773">
                  <Key className="w-5 h-5 text-gray-400" data-id="element-2774" />
                  <span data-id="element-2775">Şifre Değiştir</span>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-500" data-id="element-2776" />
              </button>
              <button onClick={() => setShowEmailModal(true)} className="w-full flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors" data-id="element-2777">
                <div className="flex items-center gap-3" data-id="element-2778">
                  <Mail className="w-5 h-5 text-gray-400" data-id="element-2779" />
                  <span data-id="element-2780">E-posta Güncelle</span>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-500" data-id="element-2781" />
              </button>
              <h3 className="text-lg font-medium pt-4" data-id="element-2782">Abonelik</h3>
              <button onClick={() => navigate('/subscription')} className="w-full flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors" data-id="element-2783">
                <div className="flex items-center gap-3" data-id="element-2784">
                  <CreditCard className="w-5 h-5 text-gray-400" data-id="element-2785" />
                  <span data-id="element-2786">Abonelik Bilgileri</span>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-500" data-id="element-2787" />
              </button>
            </div>
            <div className="mt-6 pt-6 border-t border-gray-800" data-id="element-2788">
              <button onClick={() => setShowLogoutModal(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-red-400" data-id="element-2789">
                <LogOut className="w-4 h-4" data-id="element-2790" />
                Çıkış Yap
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Badge Detail Modal */}
      {showBadgeDetails !== null && <div className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50" data-id="element-2791">
          <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600 w-full max-w-md" data-id="element-2792">
            <div className="relative bg-black/90 rounded-xl p-6 badge-details-content" data-id="element-2793">
              {(() => {
            const badge = achievements.find(b => b.id === showBadgeDetails);
            if (!badge) return null;
            const tierStyle = getTierStyle(badge.tier, badge.unlocked);
            return <div className="flex flex-col items-center space-y-4" data-id="element-2794">
                    <button onClick={() => setShowBadgeDetails(null)} className="absolute top-2 right-2 p-1 bg-white/10 hover:bg-white/20 rounded-full" data-id="element-2795">
                      <X className="w-5 h-5" data-id="element-2796" />
                    </button>
                    <div className={`relative w-24 h-24 ${tierStyle.bg} rounded-full flex items-center justify-center mb-2 ring-4 ${tierStyle.ring}`} data-id="element-2797">
                      <badge.icon className={`w-12 h-12 ${tierStyle.text}`} data-id="element-2798" />
                      {!badge.unlocked && <div className="absolute -bottom-2 -right-2 bg-gray-800 rounded-full p-1" data-id="element-2799">
                          <Lock className="w-5 h-5 text-gray-500" data-id="element-2800" />
                        </div>}
                    </div>
                    <div className="text-center" data-id="element-2801">
                      <h3 className="text-xl font-bold" data-id="element-2802">{badge.name}</h3>
                      <p className="text-gray-400 mt-1" data-id="element-2803">
                        {badge.tier === 'bronze' ? 'Bronz' : badge.tier === 'silver' ? 'Gümüş' : 'Altın'}{' '}
                        Seviye
                      </p>
                    </div>
                    {badge.unlocked ? <div className="text-center space-y-2" data-id="element-2804">
                        <div className="flex items-center justify-center gap-2 text-green-500" data-id="element-2805">
                          <CheckCircle className="w-5 h-5" data-id="element-2806" />
                          <span data-id="element-2807">Kazanıldı</span>
                        </div>
                        {badge.unlockedDate && <p className="text-gray-400 text-sm" data-id="element-2808">
                            Kazanılma Tarihi:{' '}
                            {new Date(badge.unlockedDate).toLocaleDateString('tr-TR')}
                          </p>}
                        <p className="text-gray-300 mt-2 py-2 px-4 bg-white/5 rounded-lg" data-id="element-2809">
                          {badge.description}
                        </p>
                        {badge.nextTier && <div className="mt-4 pt-4 border-t border-gray-800" data-id="element-2810">
                            <p className="text-gray-400 text-sm" data-id="element-2811">
                              Sıradaki Rozet:
                            </p>
                            <div className="flex items-center justify-center gap-2 mt-2" data-id="element-2812">
                              <div className="w-6 h-6 bg-white/10 rounded-full flex items-center justify-center" data-id="element-2813">
                                <badge.icon className="w-3 h-3 text-gray-400" data-id="element-2814" />
                              </div>
                              <span className="text-gray-300" data-id="element-2815">
                                {badge.nextTier.name}
                              </span>
                              <span className="text-gray-500 text-sm" data-id="element-2816">
                                ({badge.progress}/{badge.nextTier.target})
                              </span>
                            </div>
                          </div>}
                      </div> : <div className="text-center space-y-3" data-id="element-2817">
                        <div className="flex items-center justify-center gap-2 text-gray-400" data-id="element-2818">
                          <Lock className="w-5 h-5" data-id="element-2819" />
                          <span data-id="element-2820">Henüz Kazanılmadı</span>
                        </div>
                        <p className="text-gray-300 py-2 px-4 bg-white/5 rounded-lg" data-id="element-2821">
                          Bu rozeti kazanmak için: {badge.description}
                        </p>
                        <div className="w-full bg-gray-800 rounded-full h-2 overflow-hidden" data-id="element-2822">
                          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 h-full" style={{
                    width: `${Math.min(100, badge.progress / badge.target * 100)}%`
                  }} data-id="element-2823"></div>
                        </div>
                        <p className="text-gray-400 text-sm" data-id="element-2824">
                          İlerleme: {badge.progress}/{badge.target} (
                          {Math.round(badge.progress / badge.target * 100)}%)
                        </p>
                      </div>}
                  </div>;
          })()}
            </div>
          </div>
        </div>}
      {/* Badge Unlock Celebration Modal */}
      {showUnlockMoment && unlockedBadge && <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50" data-id="element-2825">
          <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600 w-full max-w-md animate-[bounceIn_0.5s_ease-out]" data-id="element-2826">
            <div className="relative bg-black/90 rounded-xl p-8 unlock-moment-content" data-id="element-2827">
              <div className="flex flex-col items-center space-y-6 text-center" data-id="element-2828">
                <div className="absolute top-0 left-0 w-full h-full overflow-hidden" data-id="element-2829">
                  <div className="absolute -inset-x-1/2 -top-1/2 h-[500px] w-[1000px] opacity-20 bg-gradient-to-r from-yellow-400 via-purple-500 to-pink-500 blur-3xl animate-[pulse_3s_infinite]" data-id="element-2830"></div>
                </div>
                <div className="relative z-10" data-id="element-2831">
                  <div className="text-2xl font-bold text-white mb-2" data-id="element-2832">
                    Yeni Başarım Kazandın!
                  </div>
                  <div className="relative" data-id="element-2833">
                    <div className="absolute inset-0 rounded-full animate-[ping_1s_cubic-bezier(0,0,0.2,1)_infinite] bg-yellow-500/30" data-id="element-2834"></div>
                    <div className="absolute inset-2 rounded-full animate-[ping_1.5s_cubic-bezier(0,0,0.2,1)_infinite] bg-yellow-500/20" data-id="element-2835"></div>
                    <div className="relative w-32 h-32 mx-auto bg-gradient-to-b from-yellow-500 to-amber-600 rounded-full flex items-center justify-center ring-4 ring-yellow-500/50 shadow-[0_0_15px_rgba(234,179,8,0.5)]" data-id="element-2836">
                      <unlockedBadge.icon className="w-16 h-16 text-white" data-id="element-2837" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold mt-6 text-yellow-400" data-id="element-2838">
                    {unlockedBadge.name}
                  </h3>
                  <p className="text-gray-300 mt-2" data-id="element-2839">
                    {unlockedBadge.description}
                  </p>
                  <div className="mt-8" data-id="element-2840">
                    <button onClick={() => setShowUnlockMoment(false)} className="px-6 py-3 bg-gradient-to-r from-yellow-500 to-amber-600 rounded-lg text-white font-medium hover:opacity-90 transition-colors" data-id="element-2841">
                      Harika!
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}
      {/* Password Change Modal */}
      {showPasswordModal && <PasswordChangeModal onClose={() => setShowPasswordModal(false)} onSuccess={handlePasswordChangeSuccess} data-id="element-2842" />}
      {/* Email Update Modal */}
      {showEmailModal && <EmailUpdateModal onClose={() => setShowEmailModal(false)} onSuccess={handleEmailUpdateSuccess} currentEmail={profileData.email} data-id="element-2843" />}
      {/* Logout Confirmation Modal */}
      {showLogoutModal && <LogoutConfirmModal onClose={() => setShowLogoutModal(false)} onConfirm={handleLogout} data-id="element-2844" />}
      {/* Toast notifications */}
      <ToastContainer data-id="element-2845" />
    </div>;
}